import { apiFetch } from './apiClient';
import {
  ACTIVE_STAGES, type Lead, type LeadActivity, type LeadSource, type LeadStage, type LeadTask,
  type LostReason, type NewLeadInput, type NewLeadTaskInput, type Sector,
} from '../types/lead.types';

// ─── Logica pura (testabile senza rete) ───────────────────────────────

/** Prossimo stage attivo dopo quello dato, o null se è già l'ultimo prima di won/lost. */
export function getNextStage(stage: LeadStage): LeadStage | null {
  const idx = ACTIVE_STAGES.indexOf(stage);
  if (idx === -1 || idx === ACTIVE_STAGES.length - 1) return null;
  return ACTIVE_STAGES[idx + 1];
}

export function isTerminalStage(stage: LeadStage): boolean {
  return stage === 'won' || stage === 'lost';
}

/** Un lead può essere spostato manualmente solo tra gli stage attivi — 'won'
 * passa SOLO da convertToMandate() (genera il mandato collegato), mai da
 * un cambio di stage diretto (v. firestore.rules, stesso vincolo lato server). */
export function canSetStageDirectly(stage: LeadStage): boolean {
  return stage !== 'won';
}

// ─── Filtri board (puri, testabili) ────────────────────────────────────────

export interface LeadFilters {
  search: string;
  onlyMine: boolean;
  currentUid: string;
  sector: Sector | '';
  /** Canale di acquisizione (v. SOURCE_OPTIONS) — indipendente da "solo i
   * miei", che filtra per proprietario e già include tutti i canali; questo
   * filtra per canale a prescindere dal proprietario (17/9/2026, richiesta
   * esplicita di poter distinguere i lead per canale sulla board). */
  source: LeadSource | '';
}

export const EMPTY_FILTERS: LeadFilters = { search: '', onlyMine: false, currentUid: '', sector: '', source: '' };

export function filterLeads(leads: Lead[], filters: LeadFilters): Lead[] {
  const search = filters.search.trim().toLowerCase();
  return leads.filter((lead) => {
    if (filters.onlyMine && lead.ownerId !== filters.currentUid) return false;
    if (filters.sector && lead.sector !== filters.sector) return false;
    if (filters.source && lead.source !== filters.source) return false;
    if (search && !lead.organizationName.toLowerCase().includes(search)) return false;
    return true;
  });
}

// ─── Statistiche pipeline (pure, testabili) ────────────────────────────────

export interface PipelineStats {
  activeCount: number;
  activeValue: number;
  wonCount: number;
  lostCount: number;
  /** null finché non c'è ancora nessuna chiusura (vinto+perso = 0) — non 0,
   * per distinguere "nessun dato" da "0% di successo". */
  winRate: number | null;
}

export function computePipelineStats(leads: Lead[]): PipelineStats {
  let activeCount = 0, activeValue = 0, wonCount = 0, lostCount = 0;
  for (const lead of leads) {
    if (lead.stage === 'won') wonCount++;
    else if (lead.stage === 'lost') lostCount++;
    else {
      activeCount++;
      activeValue += lead.estimatedDealValue ?? 0;
    }
  }
  const closed = wonCount + lostCount;
  return { activeCount, activeValue, wonCount, lostCount, winRate: closed > 0 ? wonCount / closed : null };
}

// ─── CRUD (API Arcanum2, Postgres) ───────────────────────────────────────

const POLL_MS = 15_000;
type Refresher = () => void;
const refreshers = new Set<Refresher>();

/** Dopo ogni modifica si rileggono subito tutte le liste aperte, senza aspettare il prossimo giro. */
function notifyChange(): void {
  refreshers.forEach((r) => r());
}

/** Lista che si aggiorna da sola ogni 15 secondi e dopo ogni modifica fatta da questa scheda. */
function subscribe<T>(load: () => Promise<T>, callback: (value: T) => void): () => void {
  let active = true;
  const run = () => { load().then((v) => { if (active) callback(v); }).catch(() => { /* resta l'ultimo valore */ }); };
  refreshers.add(run);
  run();
  const timer = setInterval(run, POLL_MS);
  return () => { active = false; refreshers.delete(run); clearInterval(timer); };
}

export function subscribeToLeads(callback: (leads: Lead[]) => void): () => void {
  return subscribe(() => apiFetch<Lead[]>('/crm-leads'), callback);
}

export async function createLead(input: NewLeadInput, _createdBy: string): Promise<string> {
  const { id } = await apiFetch<{ id: string }>('/crm-leads', { method: 'POST', body: JSON.stringify(input) });
  notifyChange();
  return id;
}

export async function updateLead(leadId: string, patch: Partial<Lead>): Promise<void> {
  await apiFetch(`/crm-leads/${leadId}`, { method: 'PATCH', body: JSON.stringify(patch) });
  notifyChange();
}

/** Riservato ad arcanum_admin — un lead già convertito porta convertedMandateId, la UI chiede conferma prima di chiamarla. */
export async function deleteLead(leadId: string): Promise<void> {
  await apiFetch(`/crm-leads/${leadId}`, { method: 'DELETE' });
  notifyChange();
}

/** Sposta il lead su uno stage attivo o 'lost' (mai 'won', v. canSetStageDirectly). */
export async function setStage(leadId: string, stage: LeadStage, lostReason?: LostReason): Promise<void> {
  if (!canSetStageDirectly(stage)) {
    throw new Error("Lo stage 'won' si imposta solo tramite convertToMandate().");
  }
  await apiFetch(`/crm-leads/${leadId}/stage`, { method: 'POST', body: JSON.stringify({ stage, lostReason }) });
  notifyChange();
}

// ─── Attività (note/chiamate/email/meeting) ────────────────────────────────

export function subscribeToActivities(leadId: string, callback: (activities: LeadActivity[]) => void): () => void {
  return subscribe(() => apiFetch<LeadActivity[]>(`/crm-leads/${leadId}/activities`), callback);
}

export async function addActivity(leadId: string, type: LeadActivity['type'], text: string, _createdBy: string): Promise<void> {
  await apiFetch(`/crm-leads/${leadId}/activities`, { method: 'POST', body: JSON.stringify({ type, text }) });
  notifyChange();
}

// ─── Task (promemoria con scadenza, distinti dalle note libere) ───────────

export function subscribeToTasks(leadId: string, callback: (tasks: LeadTask[]) => void): () => void {
  return subscribe(() => apiFetch<LeadTask[]>(`/crm-leads/${leadId}/tasks`), callback);
}

export async function addTask(leadId: string, input: NewLeadTaskInput, _createdBy: string): Promise<void> {
  await apiFetch(`/crm-leads/${leadId}/tasks`, { method: 'POST', body: JSON.stringify(input) });
  notifyChange();
}

export async function setTaskDone(leadId: string, taskId: string, done: boolean): Promise<void> {
  await apiFetch(`/crm-leads/${leadId}/tasks/${taskId}`, { method: 'PATCH', body: JSON.stringify({ done }) });
  notifyChange();
}

export async function deleteTask(leadId: string, taskId: string): Promise<void> {
  await apiFetch(`/crm-leads/${leadId}/tasks/${taskId}`, { method: 'DELETE' });
  notifyChange();
}

/** Task aperti (non completati) su TUTTI i lead, ordinati per scadenza: badge "in ritardo/oggi" e pannello "Da fare oggi". */
export function subscribeToOpenTasks(callback: (tasks: LeadTask[]) => void): () => void {
  return subscribe(() => apiFetch<LeadTask[]>('/crm-leads/tasks/open'), callback);
}

// ─── Touchpoint con la piattaforma — UNICO punto di contatto ──────────────

interface ConvertLeadResponse {
  mandateId: string;
  onboardingToken: string;
}

/**
 * Crea il mandato sulla piattaforma e genera il link di onboarding, poi segna il lead come 'won'
 * (POST /crm-leads/:id/convert-to-mandate). Unico punto in cui il CRM tocca dati della piattaforma.
 */
export async function convertToMandate(leadId: string): Promise<ConvertLeadResponse> {
  const result = await apiFetch<ConvertLeadResponse>(`/crm-leads/${leadId}/convert-to-mandate`, { method: 'POST' });
  notifyChange();
  return result;
}

export interface SourceLeadsRequest {
  /** Uno dei SOURCING_SECTOR_OPTIONS (crm/src/config/leadTaxonomy.ts) —
   * obbligatorio: un settore alla volta, non più "tutti insieme" (16/9/2026,
   * richiesta esplicita per poter focalizzare la ricerca). */
  sector: string;
  /** Sigle cantone (es. ["TI","ZH"]) — cumulabili tra loro (18/9/2026,
   * richiesta esplicita: "i cantoni devono essere cumulabili", non un
   * singolo select). Se sia `cantons` sia `cities` sono vuoti/assenti, il
   * default lato function resta il Ticino. */
  cantons?: string[];
  /** Comuni esatti (valori dalla stessa lista live restituita da
   * listComuniByCantons) — se presenti, restringono la ricerca SOLO a questi
   * comuni, indipendentemente da `cantons` per il loro cantone di
   * appartenenza (v. buildSourcingGeoSelection). */
  cities?: string[];
  /** CAP — opzionale, per prefisso (es. "69" trova sia 6900 che 6976). */
  postalCode?: string;
  /** Da un `resumeToken` di una ricerca precedente con `capped: true` — fa
   * riprendere ogni parola chiave da dove si era fermata invece di ripescare
   * sempre le stesse prime righe (18/9/2026, aggiunto per le scansioni su
   * tutta la Svizzera, dove una parola chiave generica può avere migliaia di
   * aziende candidate). */
  resumeFrom?: Record<string, number>;
}

export interface SourceLeadsResult {
  sector: string;
  cantons: string[];
  cities?: string[];
  postalCode?: string;
  keywordsProcessed: number;
  candidatesFound: number;
  duplicatesSkipped: number;
  leadsCreated: number;
  capped: boolean;
  /** Presente solo se capped e c'è altro da recuperare — passalo come
   * `resumeFrom` alla chiamata successiva per continuare la scansione. */
  resumeToken?: Record<string, number>;
}

/**
 * Un comune selezionato appartiene sempre a un cantone specifico (v.
 * listComuniByCantons) — se quel cantone è ANCHE tra quelli selezionati per
 * intero sulla mappa, il comune specifico è ridondante (il cantone intero lo
 * copre già) e va tolto dai cantoni "interi" inviati alla ricerca, altrimenti
 * la semantica sarebbe ambigua (cerca tutto il cantone E anche un suo
 * sottoinsieme, inutile). Pura e testabile: isola la logica di
 * combinazione mappa+checklist da SourcingModal.tsx (18/9/2026).
 */
export function buildSourcingGeoSelection(
  selectedCantons: string[],
  selectedCities: string[],
  cantonByCity: Record<string, string>
): { cantons: string[]; cities: string[] } {
  const cantonsWithSpecificCities = new Set(
    selectedCities.map((city) => cantonByCity[city]).filter((c): c is string => Boolean(c))
  );
  return {
    cantons: selectedCantons.filter((c) => !cantonsWithSpecificCities.has(c)),
    cities: selectedCities,
  };
}

/**
 * Agente di sourcing lead dal registro di commercio svizzero (Zefix, dati aperti): POST /crm-sourcing/run. I lead nuovi
 * compaiono nella board al prossimo aggiornamento (subito dopo la chiamata); l'arricchimento con ricerca web arriva da solo.
 */
export async function runLeadSourcing(request: SourceLeadsRequest): Promise<SourceLeadsResult> {
  const result = await apiFetch<SourceLeadsResult>('/crm-sourcing/run', { method: 'POST', body: JSON.stringify(request) });
  notifyChange();
  return result;
}

/** Elenco delle località distinte tra le aziende registrate nei cantoni indicati (checklist comuni del modulo di ricerca). */
export async function listComuniByCantons(cantons: string[]): Promise<{ canton: string; city: string }[]> {
  if (cantons.length === 0) return [];
  return apiFetch<{ canton: string; city: string }[]>('/crm-sourcing/comuni', { method: 'POST', body: JSON.stringify({ cantons }) });
}
